import { Box, Button, Typography } from "@mui/material";
import { Icon } from "components/Icons";
import { useBreak } from "hooks/useBreak";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { animated, useSpring } from "react-spring";
import { useMeasure } from "react-use";
import { getFocus, useGet } from "state/jotai";
import { active_project } from "state/store.global";
import { _project } from "state/store.projects";
import styled from "styled-components";

function WebDescription({ header, content, overlayColor, color = '#000', sx, headerStyle }) {
  const isMobile = useBreak("md_dn");
  const [isExpand, setIsExpand] = useState(false);
  const [ref, { height: contentHeight }] = useMeasure();

  const maxHeightCollapsed = isMobile ? 307 : Number.POSITIVE_INFINITY;
  const expandable = contentHeight > maxHeightCollapsed;

  const { pin, pid } = useGet(active_project);
  const buttonsLabel = getFocus(_project, 'buttonsLabel')?.[pin]?.[pid] ?? [];

  const animationStyle = useSpring({
    maxHeight: isExpand ? contentHeight + 3: maxHeightCollapsed,
    overflow: "hidden",
    config: { duration: 200 },
  });

  const handleToggle = () => {
    setIsExpand((v) => !v);
  };

  return (
    <Box
      sx={{
        fontSize: "16px",
        pb: expandable ? "42px" : 0,
        position: "relative",
        ...sx
      }}
      color={color}
    >
      <animated.div
        style={{
          ...animationStyle,
          willChange: "max-height",
          maskImage: !isExpand && expandable && "linear-gradient(to top, transparent, black 13%)",
          marginBottom: isExpand ? (isMobile ? "12px" : "24px") : 0,
        }}
      >
        <Box ref={ref}>
          <Typography variant="h2" sx={{ ...headerStyle }} className="h2">
            {header}
          </Typography>
          <DescriptionText color={color} className="paragraph1-light" dangerouslySetInnerHTML={{ __html: content }} />
        </Box>
      </animated.div>

      {expandable && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "130px",
          }}
        >
          {!isExpand && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                // Optional gradient background if needed:
                // background: `linear-gradient(0deg, ${overlayColor}8a 0%, ${overlayColor}59 70%, ${overlayColor}00 100%)`
              }}
            />
          )}
          <Button
          disableRipple
    disableFocusRipple
            className="icon-hover-effect"
            onClick={handleToggle}
            aria-label={isExpand ? "Collapse description" : "Expand description"}
            startIcon={
              <Icon
                icon={isExpand ? "unitPageDescription-arrowUp" : "unitPageDescription-arrowDown"}
                isFaIcon={false}
                sx={{ width: "40px", height: "24px" }}
              />
            }
            color="inherit"
            sx={{
              fontSize: 16,
              transition: "transform 0.3s ease-in-out",
              ".MuiButton-startIcon": {
                margin: isExpand ? 0 : "0 8px 0 -4px",
              },
              ".MuiButton-startIcon > svg": {
                fontSize: 30,
              },
              marginBottom: isExpand ? (isMobile ? 1 : 2) : (isMobile ? 2 : 4),
            }}
          />
        </Box>
      )}
    </Box>
  );
}

WebDescription.propTypes = {
  header: PropTypes.string,
  content: PropTypes.string,
  overlayColor: PropTypes.string,
  color: PropTypes.string,
};

WebDescription.defaultProps = {
  header: "",
  content: "",
  overlayColor: "#ffffff",
  color: "#000000",
};

const DescriptionText = styled.div`
  position: relative;
  p {
    color: ${({ color }) => color} !important;
    // font-family: Cerebri Sans, sans-serif !important;
  }
  span {
    color: ${({ color }) => color} !important;
    font-family: Cerebri Sans, sans-serif !important;
  }
`;

export default WebDescription;
